/**
 * Matchmaking Manager
 *
 * Handles the matchmaking flow for Quickstart:
 * - Lobby creation
 * - Lobby joining
 * - Matchmaking start
 * - Match found handling
 * - Broadcast ready detection
 *
 * This is feature-specific to Quickstart and knows about:
 * - Playcast pavilion/matchmaker system
 * - Global profile/join state stores
 * - Match timeouts and failure conditions
 */

import {
  createMatchmakerLobby,
  joinMatchmakerLobby,
  startMatchmakerLobby,
  destroyMatchmakerLobby,
} from '../../../api/modules/realtimeMessaging/handlers/profileMessage';
import { profileState, matchmakeToPublicMachineState } from '../../../api/stores/lobbyStores';
import { authStore, joinState, loginProfileState, regionOverrideState } from '../../../api/stores/globalStores';
import { playerConnectionState } from '../../../api/stores/playerStores';
import { connect, connectFastStart, getFastStartInvitationId } from '../../../api/modules/realtimeMessaging';
import { getPersistentPlayerId } from '../../../api';
import { ClientType } from '@playcastdotio/Messaging';
import { hasPreFetchedIceServers } from '../../../api/modules/iceServerPrefetch';
import { setPlayerBroadcastId } from '../../../api/modules/playerObservability';
import type { QuickstartState } from '../QuickstartController';
import type { MatchmakingDialect } from './MatchmakingDialect';
import { getPlayerGeohash, getCachedGeohash } from '../../../utility/geolocation';
import * as Sentry from '@sentry/react';

// Declare global type for auth upgrade tracking (from conectionMessage.ts)
declare global {
  interface Window {
    pendingAuthUpgradeSocket: WebSocket | null;
  }
}

export interface MatchmakingManagerConfig {
  /**
   * Get current state
   */
  getState: () => QuickstartState;

  /**
   * Update state
   */
  updateState: (state: Partial<QuickstartState>) => void;

  /**
   * Transition to a new phase
   */
  transitionTo: (phase: string) => void;

  /**
   * Called when matchmaking fails
   */
  onMatchmakingFailed: (reason: string) => void;

  /**
   * Called when broadcast ready and should connect
   */
  onBroadcastReady: (broadcastId: string) => void;

  /**
   * Called when the invitation becomes invalid during play
   */
  onInvitationInvalidated?: () => void;

  /**
   * Called when the backend reports the match ended (v2 MATCH_END).
   * Distinct from failure: the match ran (or was assigned) and is now over.
   */
  onMatchEnded?: (reason: string) => void;

  /**
   * Send analytics events
   */
  sendWebSocketConnectionStarted: () => void;
  sendLobbyCreationStarted: () => void;
  sendMatchmakingStarted: (matchmakingTimeout: number) => void;
  sendMatchmakingSucceeded: () => void;

  /**
   * Log slow sequences to Sentry
   */
  logSlowSequence?: (sequenceName: string, duration: number, context?: Record<string, unknown>) => void;

  /**
   * If true, immediately fail matchmaking when noHostsAvailable flag is received
   * If false, stay in queue and wait for a host to become available
   * Default: true
   */
  failOnNoHostsAvailable?: boolean;

  /** Optional v2 dialect. When set, matchmaking uses v2 protocol messages. */
  dialect?: MatchmakingDialect;
}

/**
 * Manager for matchmaking flow
 */
export class MatchmakingManager {
  private config: MatchmakingManagerConfig;
  private matchmakingTimeout?: NodeJS.Timeout;
  private matchFoundTimeout?: NodeJS.Timeout;
  private createdInvitationId?: string;
  private storeListeners: string[] = [];
  private debugStatusInterval?: NodeJS.Timeout;

  // Shadow architecture: Track invitation status independently
  private trackedInvitationId?: string;
  private isInvitationInvalid = false;

  constructor(config: MatchmakingManagerConfig) {
    this.config = config;
  }

  public setDialect(dialect: MatchmakingDialect): void {
    this.config.dialect = dialect;
  }

  /**
   * Start the matchmaking flow
   */
  public async startMatchmaking(): Promise<void> {
    // Clear stale no-hosts flags from previous attempts
    profileState.updateState({
      ...profileState.currentState,
      noHostsAvailable: false,
      matchFailed: undefined,
      matchEnded: undefined,
    });

    // Subscribe to stores for match notifications
    this.setupStoreSubscriptions();

    // Start debug status logging (every 1 second)
    this.startDebugStatusLogging();

    // Transition to creating lobby
    this.config.transitionTo('creating_lobby');

    // Track lobby creation start time
    const lobbyCreationStartTime = Date.now();
    this.config.updateState({ lobbyCreationStartTime });
    this.config.sendLobbyCreationStarted();

    // Wait for auth if needed
    await this.waitForAuth();

    // Get campaign ID from theme (guaranteed to be loaded by now)
    const state = this.config.getState();
    if (!state.theme) {
      console.error('[MatchmakingManager] Theme not loaded! Cannot start matchmaking.');
      this.failMatchmaking('Campaign theme not loaded');
      return;
    }
    const campaignId = state.theme.slug;
    console.log('[MatchmakingManager] Starting matchmaking for campaign:', campaignId);

    // Get player geolocation (cached or fresh)
    let geohash: string | undefined;
    try {
      // Try to use cached first (fast path)
      const cached = getCachedGeohash();
      if (cached) {
        geohash = cached.hash;
        console.log('[MatchmakingManager] Using cached geolocation:', geohash);
      } else {
        // Fetch fresh geolocation (may take up to 5s)
        const result = await getPlayerGeohash();
        geohash = result.hash;
        console.log('[MatchmakingManager] Fetched fresh geolocation:', geohash, `via ${result.method}`);
      }
    } catch (err) {
      console.warn('[MatchmakingManager] Failed to get geolocation, proceeding without:', err);
      // Continue without geolocation - matchmaking will use global fallback
    }

    // Create lobby with campaign ID and geohash
    if (this.config.dialect?.version === 'v2') {
      this.config.dialect.sendCreateRequest(campaignId, geohash);
    } else {
      createMatchmakerLobby(campaignId, geohash);
    }
  }

  /**
   * Start matchmaking from a completed fast-start connection.
   * Skips lobby creation/join/queue steps since the server already handled them.
   * Jumps directly to the "matchmaking" (waiting for match) phase.
   */
  public startFromFastStart(invitationId: string): void {
    console.log('[MatchmakingManager] Starting from fast-start, invitation:', invitationId);

    // Clear stale no-hosts flags from previous attempts
    profileState.updateState({
      ...profileState.currentState,
      noHostsAvailable: false,
      matchFailed: undefined,
      matchEnded: undefined,
    });

    this.setupStoreSubscriptions();
    this.startDebugStatusLogging();

    this.createdInvitationId = invitationId;
    this.trackedInvitationId = invitationId;

    this.config.updateState({
      lobbyCreated: true,
      lobbyJoined: true,
      lobbyCreationStartTime: Date.now(),
      lobbyCreationDuration: 0,
    });

    this.config.transitionTo('matchmaking');

    const startTime = Date.now();
    const matchmakingTimeoutSeconds = 10;
    this.config.updateState({ matchmakingStartTime: startTime });
    this.config.sendMatchmakingStarted(matchmakingTimeoutSeconds);

    this.matchmakingTimeout = setTimeout(() => {
      if (this.config.getState().phase === 'matchmaking') {
        this.failMatchmaking('Matchmaking timeout - no available hosts');
      }
    }, matchmakingTimeoutSeconds * 1000);

    // Catch noHostsAvailable/matchFailed that arrived before subscription was active
    if (profileState.currentState.noHostsAvailable || profileState.currentState.matchFailed) {
      this.handleNoHostsAvailable();
      return;
    }
  }

  /**
   * Initiate the fast-start connection flow.
   * Disconnects any existing WebSocket, reconnects with CONN_FAST_START containing
   * all auth + matchmaking data, then waits for the server result before transitioning.
   */
  public async startFastStartMatchmaking(): Promise<void> {
    // Fast-start is v1-only (CONN_FAST_START). When the v2 dialect is active,
    // open a plain WebSocket (useWebSocket skips this for quickstart when
    // USE_FAST_START is true) then fall through to the standard create→queue flow.
    if (this.config.dialect?.version === 'v2') {
      console.log('[MatchmakingManager] v2 dialect active, skipping fast-start → using standard flow');
      connect();
      return this.startMatchmaking();
    }

    const state = this.config.getState();
    if (!state.theme) {
      console.error('[MatchmakingManager] Theme not loaded! Cannot start fast-start.');
      this.failMatchmaking('Campaign theme not loaded');
      return;
    }

    // Clear stale no-hosts flags from previous attempts
    profileState.updateState({
      ...profileState.currentState,
      noHostsAvailable: false,
      matchFailed: undefined,
      matchEnded: undefined,
    });

    const campaignId = state.theme.slug;
    console.log('[MatchmakingManager] Starting fast-start matchmaking for campaign:', campaignId);
    this.config.transitionTo('creating_lobby');
    this.config.updateState({ lobbyCreationStartTime: Date.now() });
    this.config.sendLobbyCreationStarted();

    let geohash: string | undefined;
    try {
      const cached = getCachedGeohash();
      if (cached) {
        geohash = cached.hash;
      } else {
        const result = await getPlayerGeohash();
        geohash = result.hash;
      }
    } catch {
      // Continue without geolocation
    }

    const displayName =
      loginProfileState.currentState.name ||
      profileState.currentState.profile?.displayName ||
      'Anonymous';

    this.config.sendWebSocketConnectionStarted();
    connectFastStart({
      clientType: ClientType.PLAYER,
      persistentId: getPersistentPlayerId(),
      tokenJwt: authStore.currentState.accessToken || undefined,
      anonymous: !authStore.currentState.accessToken,
      campaignId,
      playerInfo: {
        displayName,
        geohash,
      },
      sessionPreset: {
        authRequired: false,
        defaultPlayerSettings: {
          gamepad: true,
          keyboard: false,
          mouse: false,
        },
      },
      matchmakeToPublicMachine: matchmakeToPublicMachineState.currentState.matchmakeToPublicMachine,
      regionOverride: regionOverrideState.currentState.regionOverride || undefined,
      requestIceServers: !hasPreFetchedIceServers(),
    });

    try {
      const invitationId = await this.waitForFastStartResult();
      this.startFromFastStart(invitationId);
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'Fast-start failed';
      console.error('[MatchmakingManager] Fast-start failed:', reason);
      this.failMatchmaking(`Fast-start failed: ${reason}`);
    }
  }

  /**
   * Wait for the fast-start server response.
   * Resolves with the invitation ID on success, rejects on error or timeout.
   */
  private waitForFastStartResult(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const FAST_START_TIMEOUT_MS = 10_000;
      let settled = false;

      const cleanup = () => {
        settled = true;
        clearTimeout(timeout);
        joinState.removeListener(joinListener);
      };

      const timeout = setTimeout(() => {
        if (!settled) {
          cleanup();
          reject(new Error('Fast-start timeout'));
        }
      }, FAST_START_TIMEOUT_MS);

      const joinListener = joinState.addListener((state) => {
        if (settled) return;

        if (state.state === 'inQueue') {
          const invitationId = getFastStartInvitationId();
          if (invitationId) {
            cleanup();
            resolve(invitationId);
          }
        } else if (state.state === 'error') {
          cleanup();
          reject(new Error('Fast-start server error'));
        }
      });
    });
  }

  /**
   * Wait for auth to be ready if user is signed in
   *
   * Shadow architecture: Detect and wait for auth upgrades to prevent race condition
   * where invitations are created with anonymous owner while upgrade is in flight
   */
  private async waitForAuth(): Promise<void> {
    return new Promise((resolve) => {
      // Check if auth upgrade is in progress (race condition prevention)
      if (typeof window !== 'undefined' && window.pendingAuthUpgradeSocket) {
        console.log('[MatchmakingManager] Auth upgrade in progress, waiting for completion...');

        // Wait for AUTH_UPGRADE_SUCCESS by monitoring playerAuthId changes
        let upgradeCheckCount = 0;
        const maxUpgradeChecks = 20; // 2 seconds max (20 * 100ms)

        const checkUpgradeComplete = () => {
          upgradeCheckCount++;

          // Check if upgrade completed (pendingAuthUpgradeSocket cleared)
          if (!window.pendingAuthUpgradeSocket) {
            console.log('[MatchmakingManager] Auth upgrade completed, playerAuthId:', authStore.currentState.playerAuthId);
            resolve();
            return;
          }

          // Timeout after max checks
          if (upgradeCheckCount >= maxUpgradeChecks) {
            console.warn('[MatchmakingManager] Auth upgrade timeout, proceeding anyway');
            resolve();
            return;
          }

          // Check again in 100ms
          setTimeout(checkUpgradeComplete, 100);
        };

        checkUpgradeComplete();
        return;
      }

      // Check if we already have auth
      if (authStore.currentState.playerAuthId) {
        console.log('[MatchmakingManager] Auth ready:', authStore.currentState.playerAuthId);
        resolve();
        return;
      }

      console.log('[MatchmakingManager] Waiting for auth...');

      // Subscribe to auth changes
      const listenerId = authStore.addListener((state) => {
        if (state.playerAuthId) {
          authStore.removeListener(listenerId);
          console.log('[MatchmakingManager] Auth ready:', state.playerAuthId);
          resolve();
        }
      });

      // Timeout after 500ms (anonymous flow)
      setTimeout(() => {
        authStore.removeListener(listenerId);
        console.log('[MatchmakingManager] No auth (anonymous flow)');
        resolve();
      }, 500);
    });
  }

  /**
   * Start debug status logging (every 1 second).
   *
   * Idempotent: clears any existing handle before arming, so the two call sites that
   * start matchmaking cannot leave an orphaned interval running for the session's
   * lifetime.
   */
  private startDebugStatusLogging(): void {
    this.stopDebugStatusLogging();

    this.debugStatusInterval = setInterval(() => {
      const state = this.config.getState();
      const profileStore = profileState.currentState;
      const joinStore = joinState.currentState;
      const authState = authStore.currentState;

      const elapsedMatchmaking = state.matchmakingStartTime
        ? Math.round((Date.now() - state.matchmakingStartTime) / 1000)
        : 0;

      console.log('[MatchmakingManager] Status Check:', {
        phase: state.phase,
        lobbyCreated: state.lobbyCreated,
        lobbyJoined: state.lobbyJoined,
        matchId: state.matchId,
        broadcastId: state.broadcastId,
        createdInvitationId: this.createdInvitationId,
        trackedInvitationId: this.trackedInvitationId,
        matchmakingElapsedSeconds: elapsedMatchmaking,
        profileStore: {
          creating: profileStore.creating,
          selectedInvitationId: profileStore.selectedInvitationId,
          matchFoundId: profileStore.matchFoundId,
          matchBroadcastId: profileStore.matchBroadcastId,
        },
        joinStore: {
          state: joinStore.state,
        },
        authState: {
          playerAuthId: authState.playerAuthId,
        },
      });
    }, 1000);
  }

  /**
   * Stop the debug status interval.
   *
   * Called whenever matchmaking reaches a terminal state — not only at teardown — so
   * the 1 Hz nested-object log does not run for the entire streaming session.
   */
  private stopDebugStatusLogging(): void {
    if (this.debugStatusInterval) {
      clearInterval(this.debugStatusInterval);
      this.debugStatusInterval = undefined;
    }
  }

  /**
   * Terminal failure: stop the debug interval, then report the failure upward.
   * Every failure path goes through here so none of them can leave the interval armed.
   */
  private failMatchmaking(reason: string): void {
    this.stopDebugStatusLogging();
    this.config.onMatchmakingFailed(reason);
  }

  /**
   * Setup subscriptions to global stores
   */
  private setupStoreSubscriptions(): void {
    // Profile state (lobby creation, match found, broadcast ready)
    const profileListenerId = profileState.addListener((state) => {
      this.handleProfileStateChange(state);
    });
    this.storeListeners.push(profileListenerId);

    // Join state (connection status)
    const joinListenerId = joinState.addListener((state) => {
      this.handleJoinStateChange(state);
    });
    this.storeListeners.push(joinListenerId);

    // Player connection state
    const playerListenerId = playerConnectionState.addListener((state) => {
      this.handlePlayerConnectionStateChange(state);
    });
    this.storeListeners.push(playerListenerId);
  }

  /**
   * Handle profile state changes
   */
  private handleProfileStateChange(state: typeof profileState.currentState): void {
    const currentState = this.config.getState();
    const currentPhase = currentState.phase;

    // Check if our tracked invitation was explicitly invalidated by backend
    // The backend sends MATCHMAKER_INVITATION_UPDATED with invalid:true which sets lastInvalidatedInvitationId
    const stateWithInvalidation = state as typeof state & { lastInvalidatedInvitationId?: string };
    if (stateWithInvalidation.lastInvalidatedInvitationId === this.trackedInvitationId && !this.isInvitationInvalid) {
      console.log('[MatchmakingManager] Tracked invitation invalidated by backend:', this.trackedInvitationId);
      this.isInvitationInvalid = true;

      // During play - exit to CTA
      if (currentPhase === 'playing_afk' || currentPhase === 'playing_active') {
        console.log('[MatchmakingManager] Invitation invalidated during play - triggering exit to CTA');
        if (this.config.onInvitationInvalidated) {
          this.config.onInvitationInvalidated();
        }
      }
      // During matchmaking - fail matchmaking
      else if (currentPhase === 'matchmaking' || currentPhase === 'match_found' || currentPhase === 'joining_broadcast') {
        console.log('[MatchmakingManager] Invitation invalidated during matchmaking - failing');
        this.failMatchmaking('Invitation was invalidated');
      }
    }

    // Surface queue position for the queue UI (v2 QUEUE_STATUS)
    if (state.queuePosition !== undefined && state.queuePosition !== currentState.queuePosition) {
      this.config.updateState({ queuePosition: state.queuePosition });
    }

    // Match explicitly failed (v2 MATCH_FAILED): surface reason, honor canRetry.
    // Distinct from noHostsAvailable (capacity hint) and from MATCH_END (ended ≠ failed).
    if (
      state.matchFailed &&
      (currentPhase === 'matchmaking' || currentPhase === 'match_found')
    ) {
      this.handleMatchFailed(state.matchFailed);
      return;
    }

    // Match ended (v2 MATCH_END): tear down and return to a neutral state.
    if (state.matchEnded && this.isMatchEndRelevant(state.matchEnded.matchRequestId, currentPhase)) {
      console.log('[MatchmakingManager] Match ended:', state.matchEnded.matchRequestId, 'reason:', state.matchEnded.reason);
      this.clearMatchmakingTimeouts();
      this.config.onMatchEnded?.(state.matchEnded.reason);
      return;
    }

    // Check if no hosts available (capacity hint while queued)
    if (state.noHostsAvailable && currentPhase === 'matchmaking') {
      this.handleNoHostsAvailable();
    }

    // Lobby created
    if (!currentState.lobbyCreated && state.creating === false && state.selectedInvitationId) {
      const lobbyCreatedTime = Date.now();
      const lobbyCreationDuration = currentState.lobbyCreationStartTime
        ? lobbyCreatedTime - currentState.lobbyCreationStartTime
        : 0;

      console.log('[MatchmakingManager] Lobby created:', state.selectedInvitationId, `(${lobbyCreationDuration}ms)`);

      this.createdInvitationId = state.selectedInvitationId;
      this.trackedInvitationId = state.selectedInvitationId; // Track this invitation
      this.config.updateState({
        lobbyCreated: true,
        lobbyCreationDuration
      });

      // Log to Sentry if > 3 seconds
      if (this.config.logSlowSequence) {
        const totalDuration = currentState.pageLoadTime ? lobbyCreatedTime - currentState.pageLoadTime : undefined;
        this.config.logSlowSequence('lobby_creation', lobbyCreationDuration, {
          totalDuration,
          invitationId: state.selectedInvitationId,
        });
      }

      this.joinLobby();
    }

    // Match found
    if (
      state.matchFoundId &&
      !currentState.matchId &&
      currentPhase === 'matchmaking' &&
      state.matchFoundId === state.selectedInvitationId
    ) {
      console.log('[MatchmakingManager] Match found:', state.matchFoundId);
      this.handleMatchFound(state.matchFoundId);
    }

    // Broadcast ready
    if (
      state.matchBroadcastId &&
      !currentState.broadcastId &&
      currentPhase === 'match_found' &&
      state.matchFoundId === currentState.matchId
    ) {
      console.log('[MatchmakingManager] Broadcast ready:', state.matchBroadcastId);
      this.handleBroadcastReady(state.matchBroadcastId);
    }
  }

  /**
   * Handle join state changes
   */
  private handleJoinStateChange(state: typeof joinState.currentState): void {
    if (state.state === 'hostDisconnected' || state.state === 'error') {
      const currentPhase = this.config.getState().phase;
      if (currentPhase === 'joining_broadcast') {
        console.error('[MatchmakingManager] Connection failed via joinState');
      }
    }
  }

  /**
   * Handle player connection state changes
   */
  private handlePlayerConnectionStateChange(state: typeof playerConnectionState.currentState): void {
    if (state.status === 'connected') {
      const currentPhase = this.config.getState().phase;
      if (currentPhase === 'joining_broadcast') {
        this.config.updateState({ playerConnectionId: state.playerPeerId });
      }
    }
  }

  /**
   * Whether a MATCH_END for the given match request should be acted on now.
   * Only relevant while this flow is tracking the request (queue → play);
   * stale ends from a previous attempt are ignored.
   */
  private isMatchEndRelevant(matchRequestId: string, currentPhase: string): boolean {
    if (this.trackedInvitationId && matchRequestId !== this.trackedInvitationId) {
      return false;
    }
    return (
      currentPhase === 'matchmaking' ||
      currentPhase === 'match_found' ||
      currentPhase === 'joining_broadcast' ||
      currentPhase === 'preparing_player' ||
      currentPhase === 'playing_afk' ||
      currentPhase === 'playing_active'
    );
  }

  /**
   * Handle explicit match failure (v2 MATCH_FAILED): surface the reason and
   * whether the player may requeue. Always fails the flow — unlike
   * noHostsAvailable, this is a terminal answer from the backend.
   */
  private handleMatchFailed(failure: { reason: string; canRetry: boolean }): void {
    console.log('[MatchmakingManager] Match failed:', failure.reason, 'canRetry:', failure.canRetry);
    this.clearMatchmakingTimeouts();
    this.destroyInvitation();
    this.config.updateState({ matchCanRetry: failure.canRetry });
    this.config.onMatchmakingFailed(failure.reason);
  }

  /**
   * Clear both matchmaking-related timeouts
   */
  private clearMatchmakingTimeouts(): void {
    if (this.matchmakingTimeout) {
      clearTimeout(this.matchmakingTimeout);
      this.matchmakingTimeout = undefined;
    }
    if (this.matchFoundTimeout) {
      clearTimeout(this.matchFoundTimeout);
      this.matchFoundTimeout = undefined;
    }
  }

  /**
   * Handle no-hosts-available condition (from listener or immediate check).
   * Clears the matchmaking timeout, reports to Sentry, and fails matchmaking.
   */
  private handleNoHostsAvailable(): void {
    const failOnNoHosts = this.config.failOnNoHostsAvailable !== false;
    const currentState = this.config.getState();

    console.error('[MatchmakingManager] No hosts available for matchmaking for user', {
      failOnNoHosts,
      invitationId: profileState.currentState.selectedInvitationId,
      campaignId: currentState.theme?.slug,
      phase: currentState.phase,
      geoHash: getCachedGeohash()?.hash || 'unknown',
    });

    Sentry.captureException(new Error('No hosts available for matchmaking for user'), {
      level: 'warning',
      tags: {
        component: 'matchmaking_manager',
        campaign: currentState.theme?.slug || 'unknown',
        action: failOnNoHosts ? 'failing' : 'queuing',
        geoHash: getCachedGeohash()?.hash || 'unknown',
      },
      contexts: {
        matchmaking: {
          invitationId: profileState.currentState.selectedInvitationId,
          campaignId: currentState.theme?.slug,
          phase: currentState.phase,
          failOnNoHosts,
          matchmakingElapsed: currentState.matchmakingStartTime
            ? Date.now() - currentState.matchmakingStartTime
            : 0,
          geoHash: getCachedGeohash()?.hash || 'unknown',
        },
      },
    });

    if (failOnNoHosts) {
      if (this.matchmakingTimeout) {
        clearTimeout(this.matchmakingTimeout);
        this.matchmakingTimeout = undefined;
      }
      console.log('[MatchmakingManager] Failing matchmaking due to no available hosts');
      this.destroyInvitation();
      this.failMatchmaking('No available hosts - please try again');
    } else {
      console.log('[MatchmakingManager] Staying in queue despite no available hosts');
    }
  }

  /**
   * Join the created lobby.
   * v1 requires an explicit MATCHMAKER_JOIN_INVITATION; in v2 the creator is
   * already on the match request, so we go straight to the queue.
   */
  private joinLobby(): void {
    if (this.config.dialect?.version === 'v2') {
      this.config.updateState({ lobbyJoined: true });
      this.startMatchmakingSearch();
      return;
    }

    joinMatchmakerLobby();
    this.config.updateState({ lobbyJoined: true });
    this.startMatchmakingSearch();
  }

  /**
   * Start matchmaking search
   */
  private startMatchmakingSearch(): void {
    this.config.transitionTo('matchmaking');

    const startTime = Date.now();
    const matchmakingTimeoutSeconds = 10;
    this.config.updateState({ matchmakingStartTime: startTime });
    this.config.sendMatchmakingStarted(matchmakingTimeoutSeconds);

    // Add the request to the matchmaking queue
    if (this.config.dialect?.version === 'v2') {
      this.config.dialect.sendQueueRequest(this.createdInvitationId ?? '');
    } else {
      startMatchmakerLobby(false, undefined);
    }

    // Set timeout (10 seconds)
    this.matchmakingTimeout = setTimeout(() => {
      if (this.config.getState().phase === 'matchmaking') {
        this.failMatchmaking('Matchmaking timeout - no available hosts');
      }
    }, matchmakingTimeoutSeconds * 1000);

    // Catch noHostsAvailable/matchFailed that arrived before subscription was active
    if (profileState.currentState.noHostsAvailable || profileState.currentState.matchFailed) {
      this.handleNoHostsAvailable();
      return;
    }
  }

  /**
   * Handle match found
   */
  private handleMatchFound(matchId: string): void {
    const currentState = this.config.getState();
    const duration = currentState.matchmakingStartTime
      ? Date.now() - currentState.matchmakingStartTime
      : 0;

    // Shadow architecture: Track the matched invitation for invalidation detection
    this.trackedInvitationId = matchId;

    this.config.updateState({
      matchId,
      matchmakingDuration: duration,
      estimatedSetupTimeSeconds: profileState.currentState.estimatedSetupTimeSeconds,
    });

    this.config.transitionTo('match_found');
    this.config.sendMatchmakingSucceeded();

    // Log to Sentry if > 3 seconds
    if (this.config.logSlowSequence) {
      const totalDuration = currentState.pageLoadTime ? Date.now() - currentState.pageLoadTime : undefined;
      this.config.logSlowSequence('matchmaking', duration, {
        totalDuration,
        matchId,
      });
    }

    // Clear matchmaking timeout
    if (this.matchmakingTimeout) {
      clearTimeout(this.matchmakingTimeout);
      this.matchmakingTimeout = undefined;
    }

    // Set timeout for broadcast ready (15 seconds)
    this.matchFoundTimeout = setTimeout(() => {
      if (this.config.getState().phase === 'match_found') {
        console.error('[MatchmakingManager] Broadcast ready timeout');
        this.destroyInvitation();
        this.failMatchmaking('Match found but broadcast never became ready');
      }
    }, 15000);
  }

  /**
   * Handle broadcast ready
   */
  private handleBroadcastReady(broadcastId: string): void {
    // Matchmaking is done — stop the 1 Hz debug interval before the stream starts,
    // rather than leaving it running until full session teardown.
    this.stopDebugStatusLogging();

    // Clear match found timeout
    if (this.matchFoundTimeout) {
      clearTimeout(this.matchFoundTimeout);
      this.matchFoundTimeout = undefined;
    }

    this.config.updateState({ broadcastId });
    setPlayerBroadcastId(broadcastId);

    // Clear invitation ID - broadcast is ready
    console.log('[MatchmakingManager] Broadcast ready, clearing invitation to prevent cleanup destruction');
    this.createdInvitationId = undefined;

    // Notify coordinator to start connection
    this.config.onBroadcastReady(broadcastId);
  }

  /**
   * Retry matchmaking (after failure)
   */
  public retry(): void {
    console.log('[MatchmakingManager] Retrying matchmaking');

    // Destroy old invitation if it exists
    this.destroyInvitation();

    // Reset state
    this.config.updateState({
      lobbyCreated: false,
      lobbyJoined: false,
      matchId: undefined,
      broadcastId: undefined,
      matchmakingStartTime: undefined,
      matchmakingDuration: undefined,
      errorMessage: undefined,
      matchCanRetry: undefined,
      matchEndReason: undefined,
      queuePosition: undefined,
      estimatedSetupTimeSeconds: undefined,
    });

    // Start fresh
    this.startMatchmaking();
  }

  /**
   * Destroy the created invitation
   */
  public destroyInvitation(): void {
    if (this.config.dialect?.version === 'v2') {
      const matchRequestId = this.createdInvitationId;
      if (matchRequestId) {
        this.config.dialect.sendDestroyRequest(matchRequestId);
      }
      return;
    }

    if (this.createdInvitationId) {
      console.log('[MatchmakingManager] Destroying invitation:', this.createdInvitationId);
      try {
        destroyMatchmakerLobby(this.createdInvitationId);
      } catch (error) {
        console.error('[MatchmakingManager] Error destroying invitation:', error);
      }
      this.createdInvitationId = undefined;
    }
  }

  /**
   * Cleanup matchmaking resources
   */
  public cleanup(): void {
    // Clear debug status interval
    this.stopDebugStatusLogging();

    // Clear timeouts
    if (this.matchmakingTimeout) {
      clearTimeout(this.matchmakingTimeout);
      this.matchmakingTimeout = undefined;
    }

    if (this.matchFoundTimeout) {
      clearTimeout(this.matchFoundTimeout);
      this.matchFoundTimeout = undefined;
    }

    // Unsubscribe from stores
    if (this.storeListeners.length > 0) {
      profileState.removeListener(this.storeListeners[0]);
      joinState.removeListener(this.storeListeners[1]);
      playerConnectionState.removeListener(this.storeListeners[2]);
      this.storeListeners = [];
    }

    // Reset tracking variables
    this.trackedInvitationId = undefined;
    this.isInvitationInvalid = false;

    // Destroy invitation if still exists
    this.destroyInvitation();
  }
}

